import cabinDown from "assets/dsv-truck-cabin-down.png";
import cabinLeft from "assets/dsv-truck-cabin-left.png";
import cabinRight from "assets/dsv-truck-cabin-right.png";
import cabinTop from "assets/dsv-truck-cabin-top.png";
import trailerDown from "assets/dsv-truck-trailer-down.png";
import trailerLeft from "assets/dsv-truck-trailer-left.png";
import trailerRight from "assets/dsv-truck-trailer-right.png";
import trailerTop from "assets/dsv-truck-trailer-top.png";
import pallete from "assets/pallete.png";
import { useEffect, useRef, useState } from "react";
import { useInterval } from "../hooks/useInterval";
import { useSnakeStore } from "../store/useSnakeStore";
import "./Snake.styles.css";

const CANVAS_SIZES = {
  lg: [720, 390],
  sm: [300, 420],
};
const CANVAS_BREAKPOINT = [720, 390];
const SCALE = 30;

const DIRECTIONS = {
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
};

function Snake({ children }) {
  const highestScore = useSnakeStore((state) => state.highest);
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState(CANVAS_SIZES.lg);
  const [scale, setScale] = useState(SCALE);
  const [snake, setSnake] = useState([]);
  const [apple, setApple] = useState([]);
  const [currentDirKey, setCurrentDirKey] = useState(null);
  const [nextDir, setNextDir] = useState([1, 0]);
  const [nextDirKey, setNextDirKey] = useState("ArrowRight");
  const [speed, setSpeed] = useState(null);
  const [gameOver, setGameOver] = useState(true);
  const [score, setScore] = useState(null);
  const [activeButtons, setActiveButtons] = useState({
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false,
  });

  useInterval(() => gameLoop(), speed);

  useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth < CANVAS_BREAKPOINT[0] ||
        window.innerHeight < CANVAS_BREAKPOINT[1]
      ) {
        setCanvasSize(CANVAS_SIZES.sm);
        setScale(30);
      } else {
        setCanvasSize(CANVAS_SIZES.lg);
        setScale(SCALE);
      }
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      setActiveButtons({
        ArrowUp: false,
        ArrowDown: false,
        ArrowLeft: false,
        ArrowRight: false,
      });
    };
  }, []);

  const cellWidth = canvasSize[0] / scale;
  const cellHeight = canvasSize[1] / scale;
  const initialSnake = [
    [cellWidth / 2 - 1, cellHeight - 1],
    [cellWidth / 2 - 2, cellHeight - 1],
  ];
  const initialApple = [cellWidth / 2, cellHeight - 1];

  useEffect(() => {
    setSnake(initialSnake);
    setApple(initialApple);
    setNextDirKey("ArrowRight");
  }, [canvasSize, scale]);

  const endGame = () => {
    setSpeed(null);
    setGameOver(true);
    if (score > highestScore) useSnakeStore.setState({ highest: score });
  };

  function handleKeyDown(e) {
    setActiveButtons((prev) => ({ ...prev, [e.key]: true }));
    handleKeys(e.key);
  }

  function handleKeyUp(e) {
    setActiveButtons((prev) => ({ ...prev, [e.key]: false }));
  }

  function handleClick(e) {
    handleKeys(e.target.id);
  }

  function handleKeys(pressedKey) {
    const isDirectionKey = [
      "ArrowRight",
      "ArrowLeft",
      "ArrowUp",
      "ArrowDown",
    ].includes(pressedKey);

    if (isDirectionKey || "Enter" === pressedKey) {
      if (gameOver) startGame();
      else {
        if (
          !isDirectionKey ||
          (pressedKey === "ArrowLeft" && nextDirKey === "ArrowRight") ||
          (pressedKey === "ArrowLeft" && currentDirKey === "ArrowRight") ||
          (pressedKey === "ArrowRight" && nextDirKey === "ArrowLeft") ||
          (pressedKey === "ArrowRight" && currentDirKey === "ArrowLeft") ||
          (pressedKey === "ArrowUp" && nextDirKey === "ArrowDown") ||
          (pressedKey === "ArrowUp" && currentDirKey === "ArrowDown") ||
          (pressedKey === "ArrowDown" && nextDirKey === "ArrowUp") ||
          (pressedKey === "ArrowDown" && currentDirKey === "ArrowUp")
        )
          return;

        setNextDirKey(pressedKey);
        moveSnake(pressedKey);
      }
    }
  }

  const moveSnake = (moveDirection) => setNextDir(DIRECTIONS[moveDirection]);

  const createApple = () =>
    apple.map((_a, i) => Math.floor(Math.random() * (canvasSize[i] / scale)));

  const checkCollision = (piece, snk = snake) => {
    if (
      piece[0] * scale >= canvasSize[0] ||
      piece[0] <= -1 ||
      piece[1] * scale >= canvasSize[1] ||
      piece[1] <= -1
    )
      return true;

    for (const segment of snk) {
      if (piece[0] === segment[0] && piece[1] === segment[1]) return true;
    }
    return false;
  };

  const checkAppleCollision = (newSnake) => {
    if (newSnake[0][0] === apple[0] && newSnake[0][1] === apple[1]) {
      let newApple = createApple();
      while (checkCollision(newApple, newSnake)) {
        newApple = createApple();
      }
      setApple(newApple);
      setScore((prev) => prev + 1);
      return true;
    }
    return false;
  };

  const gameLoop = () => {
    const snakeCopy = [...snake];
    const newSnakeHead = [
      snakeCopy[0][0] + nextDir[0],
      snakeCopy[0][1] + nextDir[1],
    ];
    snakeCopy.unshift(newSnakeHead);
    if (checkCollision(newSnakeHead)) endGame();
    if (!checkAppleCollision(snakeCopy)) snakeCopy.pop();
    setSnake(snakeCopy);
    setCurrentDirKey(nextDirKey);
  };

  const startGame = () => {
    setSnake(initialSnake);
    setApple(initialApple);
    setNextDir([1, 0]);
    setNextDirKey("ArrowRight");
    setScore(0);
    setSpeed(100);
    setGameOver(false);
  };

  useEffect(() => {
    const cabinRightImage = new Image();
    const cabinLeftImage = new Image();
    const trailerRightImage = new Image();
    const trailerLeftImage = new Image();
    const cabinTopImage = new Image();
    const cabinDownImage = new Image();
    const trailerTopImage = new Image();
    const trailerDownImage = new Image();
    const palleteImage = new Image();
    cabinRightImage.src = cabinRight;
    cabinLeftImage.src = cabinLeft;
    trailerRightImage.src = trailerRight;
    trailerLeftImage.src = trailerLeft;
    cabinTopImage.src = cabinTop;
    cabinDownImage.src = cabinDown;
    trailerTopImage.src = trailerTop;
    trailerDownImage.src = trailerDown;
    palleteImage.src = pallete;

    Promise.all([
      cabinRightImage,
      cabinLeftImage,
      trailerRightImage,
      trailerLeftImage,
      cabinTopImage,
      cabinDownImage,
      trailerTopImage,
      trailerDownImage,
      palleteImage,
    ]).then(
      ([
        cabinRightImg,
        cabinLeftImg,
        trailerRightImg,
        trailerLeftImg,
        cabinTopImg,
        cabinDownImg,
        trailerTopImg,
        trailerDownImg,
        palleteImg,
      ]) => {
        const context = canvasRef.current.getContext("2d");

        context.setTransform(scale, 0, 0, scale, 0, 0);
        context.clearRect(0, 0, window.innerWidth, window.innerHeight);
        snake.forEach(([x, y], idx, self) => {
          // Cabin
          if (idx === 0) {
            let headImage;
            if (nextDirKey === "ArrowUp") headImage = cabinTopImg;
            else if (nextDirKey === "ArrowDown") headImage = cabinDownImg;
            else if (nextDirKey === "ArrowRight") headImage = cabinRightImg;
            else if (nextDirKey === "ArrowLeft") headImage = cabinLeftImg;
            context.drawImage(headImage, x, y, 1, 1);
          } else {
            let [nextX, nextY] = self[idx - 1];
            let tailImage;
            if (nextX < x) tailImage = trailerLeftImg;
            else if (nextX > x) tailImage = trailerRightImg;
            else if (nextY < y) tailImage = trailerDownImg;
            else if (nextY > y) tailImage = trailerTopImg;

            context.drawImage(tailImage, x, y, 1, 1);
          }
        });
        context.drawImage(palleteImg, apple[0], apple[1], 1, 1);
      }
    );
  }, [snake, apple, gameOver, nextDirKey, scale, canvasSize]);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onTouchStart={startGame}
    >
      {score !== null && (
        <div className="text-end fw-bold px-4 py-3">
          Highest: {highestScore} Score: {score}
        </div>
      )}
      <canvas
        className={!gameOver ? "rounded-corners" : "shake"}
        ref={canvasRef}
        width={`${canvasSize[0]}px`}
        height={`${canvasSize[1]}px`}
      />

      {!gameOver ? (
        <div
          className="keys-container"
          onClick={handleClick}
          onTouchStart={handleClick}
        >
          {Object.keys(DIRECTIONS).map((key, idx) => (
            <div
              key={idx}
              id={key}
              tabIndex={idx + 1}
              role="button"
              className={`nes-btn arrow rounded-corners ${
                activeButtons[key] ? "nes-btn--active" : ""
              }`}
            />
          ))}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export default Snake;
